
import {defineComponent, ref, PropType, watch, onMounted, getCurrentInstance, nextTick, computed} from "vue";
  import { useStore } from "vuex";
  import {ElDatePicker, ElIcon, ElInput, ElSelectV2, ElConfigProvider} from "element-plus";
  import { Check, WarnTriangleFilled } from "@element-plus/icons-vue";
  import { FormActions, FormGetters } from './../../Store/index';
import {
  callPaq,
  getInputsAboveOrder,
  resetInputs, setResettingFormToFalseWhenNoRequests,
  updateStoreValueAndFilter, useShowElementByPredefinedManufacturer,
  validation
} from './../../Composables/form';
  import { isEmpty } from './../../Composables/functions';
  import de from 'element-plus/dist/locale/de.mjs';
  import dayjs from 'dayjs';
  require('dayjs/locale/de');

  // Define an enum for the valid date picker types
  enum DatePickerType {
    Year = "year",
    Month = "month",
    Date = "date"
  }

  // Define a type for the valid size values
  type ValidSize = "default" | "small" | "large";

  export default defineComponent({
    name: "DateComponent",
    components: {
      ElInput,
      ElSelectV2,
      ElDatePicker,
      ElConfigProvider,
      ElIcon,
      Check,
      WarnTriangleFilled
    },

    props: {
      value: String,
      title: String,
      format: {
        type: String,
        default: ''
      },
      valueFormat: {
        type: String,
        default: ''
      },
      classInput: String,
      classDiv: String,
      popperClass: {
        type: String,
        default: ''
      },
      suffixCalendarIcon: Boolean,
      size: {
        type: String as PropType<ValidSize>,
        required: false,
        default: "default",
      },
      name: {
        type: String,
        default: ''
      },
      type: {
        type: String as PropType<DatePickerType>,
        required: false,
        default: DatePickerType.Month,
      },
      placeholder: {
        type: String,
        default: ''
      },
      startPlaceholder: {
        type: String,
        default: ''
      },
      endPlaceholder: {
        type: String,
        default: ''
      },
      rangeSeparator: {
        type: String,
        default: ''
      },
      errorMessage: String,
      required: Boolean,
      disabled: Boolean,
      startYear: {
        type: Number,
        required: false,
        default: () => new Date().getFullYear()
      },
      endYear: {
        type: Number,
        required: false,
        default: 2012
      },
      id: {
        type: String,
        default: ''
      },
      filterProperty: {
        type: Array,
        required: false,
        default: () => ['', '']
      },
      defaultValue: {
        type: String,
        default: "",
      },
    },

    setup(props) {
      dayjs.locale('de');

        document.querySelector('body')?.classList.add('calendar-disable-button-right');

      // Access the Vuex store
      const store = useStore();
      const instance = getCurrentInstance();

      // Getters from the store
      const labels = store.getters[`form/${FormGetters.GET_LABELS}`],
          icons = store.getters[`form/${FormGetters.GET_ICONS}`],
          filterName = (props.filterProperty[1] as string).toString(),
          locale = de;

      let value = ref(props.defaultValue || ""),
          isValid = ref(false),
          isTouched = ref(false),
          isFloatingLabel = ref(false),
          clearable = ref(true),
          showError = ref(false),
          date = '',
          showElement = computed(() => useShowElementByPredefinedManufacturer(store, props.name).value),
          wasClickedOnShowResultPage = ref(store.getters[`form/${FormGetters.GET_WAS_CLICKED_ON_SHOW_RESULT_PAGE}`]);

      // Function to reset values
      const setFloatingLabel = () => {
        isFloatingLabel.value = true;
      };

      const isValidDateFormat = (dateString: string): boolean => {
        const formats = ['DD.MM.YYYY', 'D.M.YYYY', 'DD.M.YYYY', 'D.MM.YYYY'];

        for (const format of formats) {
          const parsedDate = dayjs(dateString, format, true); // true to enable strict parsing

          if (parsedDate.isValid()) {
            // If the parsed date is valid, update the date variable with the formatted date
            date = parsedDate.format('DD.MM.YYYY');
            return true;
          }
        }

        // If none of the formats match, return false
        return false;
      };

      const onPanelChange = (date: Date) => {
          const year = date.getFullYear();
          const month = date.getMonth() + 1; // months are zero-indexed

          document.querySelector('body')?.classList.remove('calendar-disable-button-left', 'calendar-disable-button-right');
          if(year >= props.startYear) {
              document.querySelector('body')?.classList.add('calendar-disable-button-right');
          } else if(year <= props.endYear) {
              document.querySelector('body')?.classList.add('calendar-disable-button-left');
          }
      }

      const onKeyDown = (e) => {
        const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.'];
        date = e.target.value;

        if (numbers.includes(e.key)) {
          date += e.key;
        }

        if (e.key === 'Enter') {
          nextTick(() => {
            if (isValidDateFormat(date)) {
              value.value = dayjs(date, 'DD.MM.YYYY').format('MM/YYYY');
            }
            date = '';
          });
        }
      }

      // Function to reset values
      const resetFloatingLabel = () => {
        if (isEmpty(value.value)) {
          isFloatingLabel.value = false;
        }
      };

      // Function to reset values
      const onBlur = () => {
        resetFloatingLabel();

        nextTick(() => {
          setTimeout(() => {
            if (isValidDateFormat(date)) {
              value.value = dayjs(date, 'DD.MM.YYYY').format('MM/YYYY');
            }
          }, 40);
        });
      };

      // Check if the component should be clearable
      const handleClearable = () => {
        clearable.value = !!value.value;
      };

      // Default value initialization
      onMounted(() => {
        if (props.defaultValue) {
          updateValue(props.defaultValue);
        }

        // _paq
        if (filterName && value.value) {
          callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'verwendete Filter', filterName);
        }
      });

      // Function to reset values
      const resetValues = () => {
        store.dispatch(`form/${FormActions.SET_IS_RESSETING_FORM}`, true);

        isValid.value = false;
        isTouched.value = false;
        value.value = '';
        date = '';

        const filter = store.getters[`form/${FormGetters.GET_DATA}`].filter.value;

        if (isEmpty(filter) || isEmpty(filter[filterName])) {
          store.dispatch(`form/${FormActions.DELETE_DATA}`, {field: 'filter', key: 'value', value: filterName});
        }

        // Reset floating label
        resetFloatingLabel();

        resetInputs(store, getInputsAboveOrder(store, props.name), 'resetValue');

        setResettingFormToFalseWhenNoRequests(store);
      };

      const showErrorMessage = () => {
        showError.value = (isTouched && !isValid) || (wasClickedOnShowResultPage.value && !isValid.value);
      };

      const isValidDate = (dateString: string) => {
        if (props.required && dateString.length <= 0) {
          return false;
        }

        // Check if the date string matches the MM/YYYY format using a regular expression
        const dateRegex = /^(0[1-9]|1[0-2])\/\d{4}$/;
        if (!dateRegex.test(dateString)) {
          return false;
        }

        // Split the date string into its components
        const [month, year] = dateString.split('/').map(Number);

        // Get the current date
        const currentDate = new Date();

        // Create a new Date object using the components
        const date = new Date(year, month - 1, 1); // Day is set to 1 as it's not relevant for validation

        // Check if the components are valid, match the original date, and the year is within the allowed range
        return (
            date.getMonth() === month - 1 && // Month is 0-indexed
            date.getFullYear() === year &&
            year >= props.endYear &&
            year <= props.startYear &&
            (year < props.startYear || month <= currentDate.getMonth() + 1)
        );
      };

      // Function to update the selected option value and validity
      const updateValue = async (newValue: any) => {
        await store.dispatch(`form/${FormActions.SET_WAS_CLICKED_ON_SHOW_RESULT_PAGE}`, false);

        if (newValue instanceof KeyboardEvent && newValue.key === 'Enter') {
          const target = newValue.target;

          // Check if the target is an input element
          if (target instanceof HTMLInputElement) {
            newValue = target.value;
          }
        }

        const predefinedValues = store.getters[`form/${FormGetters.GET_DATA}`].predefinedManufacturerValues;

        if (predefinedValues && predefinedValues.hasValue && predefinedValues[props.name]) {
          newValue = predefinedValues[props.name];
        }

        // Update the validity based on whether the value is empty or not
        isValid.value = isValidDate(newValue);

        if (isValid.value) {
          isTouched.value = true;
          value.value = isValid.value ? newValue : '';

          await updateStoreValueAndFilter(store, props, value.value, isValid.value);

          // _paq
          if (filterName && value.value && !store.getters[`form/${FormGetters.GET_IS_RESETTING_FORM}`]) {
            callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'verwendete Filter', filterName);
          }

          await validation(store);
        }

        handleClearable();
        showErrorMessage();
      }

      const disabledDate = (time: Date) => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        const selectedYear = time.getFullYear();
        const selectedMonth = time.getMonth();

        // Disable months in the future
        const disableFutureMonths = selectedYear > currentYear || (selectedYear === currentYear && selectedMonth > currentMonth);

        // Disable months based on the range of years
        const disableYearRange = selectedYear > props.startYear || selectedYear < props.endYear;

        // Combine the conditions
        return disableFutureMonths || disableYearRange || props.disabled;
      };

      // Watch for changes in the value
      watch(() => store.getters[`form/${FormGetters.GET_INPUTS}`][props.name].value, (newValue, oldValue) => {
        if (newValue === 'resetValue' || isEmpty(newValue)) {
          resetValues();
          updateValue(newValue);
        }
      });

      watch(() => store.getters[`form/${FormGetters.GET_WAS_CLICKED_ON_SHOW_RESULT_PAGE}`], async (newValue, oldValue) => {
        wasClickedOnShowResultPage.value = newValue;
        showErrorMessage();
      });

      const canShowLabel = computed(() => {
          return !props.title; // we show label only if no title is used
      });

      return {
        icons,
        labels,
        value,
        isValid,
        isTouched,
        updateValue,
        disabledDate,
        resetValues,
        isFloatingLabel,
        clearable,
        instance,
        showError,
        locale,
        showElement,
        setFloatingLabel,
        onKeyDown,
        onBlur,
        onPanelChange,
        canShowLabel
      }
    }
  })
